import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject } from '@angular/core';

import { CompanyParametersQuery, NumberingItem, NumberingQuery, PaymentMethodItem, PaymentMethodQuery, PurchaseKindBO } from '@skylab-shared/business';
import { IdentifiableValueConverter } from '@skylab-shared/core';
import { CalculationMethod, LabeledEnumValue, LabeledEnumValueConverter, PurchaseDocumentKind, PurchaseKindModel, SaleDocumentKind } from '@skylab-shared/dataModel';

import { PurchaseKindRepository, PurchaseKindRepositoryBase } from '@skylab-angular-shared/business';
import { CommandBus, EnumTranslator, EnumTranslatorBase, EventBus, EventBusBase, L10n, L10nBase, RootContext, RootContextBase, Weaver, WeaverBase } from '@skylab-angular-shared/core';
import { ButtonComponent, DialogContentComponentIns, DialogHeaderComponentIns, DialogInsService, FocusByDirective, FormContainerComponent, NumericValueAccessorDirective, SelectValueAccessorDirective, TextValueAccessorDirective, ToastService, TooltipDirective, ValidateDirective, ValidationSourceDirective, IconComponent } from '@skylab-angular-shared/ui';

import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { DictionaryDialogComponentBase, DictionaryDialogData, DictionaryDialogViewModelBase } from '../dictionary-dialog-base';

export interface PurchaseKindDialogResult {
    id: number;
    version: string;
}

interface PurchaseKindViewModel extends DictionaryDialogViewModelBase<PurchaseKindBO> {
    calculationMethods: LabeledEnumValue[];
    calculationMethodsConverter: LabeledEnumValueConverter<CalculationMethod>;
    documentName: string;
    documentKindValueConverter: LabeledEnumValueConverter<SaleDocumentKind>;
    paymentMethodsConverter: IdentifiableValueConverter | null;
    numberings: NumberingItem[];
    paymentMethods: PaymentMethodItem[];
    numberingsConverter: IdentifiableValueConverter | null;
    focusReady: boolean;
    isDefaultCalculationMethodVisible: boolean;
    isDefaultDueDateVisible: boolean;
}
@Component({
    selector: 'skl-purchase-kind-dialog',
    templateUrl: './purchase-kind-dialog.component.html',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [DialogContentComponentIns, DialogHeaderComponentIns, FormContainerComponent, ValidationSourceDirective, TextValueAccessorDirective, FormsModule, ValidateDirective, FocusByDirective, SelectValueAccessorDirective, NumericValueAccessorDirective, TooltipDirective, ButtonComponent, TranslatePipe, IconComponent]
})
export class PurchaseKindDialogComponent extends DictionaryDialogComponentBase<PurchaseKindModel, PurchaseKindBO, PurchaseKindRepositoryBase, PurchaseKindViewModel, DictionaryDialogData<string>> {
    // dependencies
    public constructor(
        @Inject(PurchaseKindRepository) purchaseKindRepositoryBase: PurchaseKindRepositoryBase,
        @Inject(L10n) l10n: L10nBase,
        @Inject(Weaver) weaver: WeaverBase,
        @Inject(RootContext) rootContext: RootContextBase,
        @Inject(EventBus) eventBus: EventBusBase,
        toastService: ToastService,
        commandBus: CommandBus,

        dialogService: DialogInsService,
        @Inject(EnumTranslator) private readonly enumTranslator: EnumTranslatorBase
    ) {
        super(purchaseKindRepositoryBase, toastService, l10n, weaver, rootContext, commandBus, eventBus, dialogService);
    }

    protected override getInitialState(): PurchaseKindViewModel {
        return {
            ...super.getInitialState(),
            calculationMethods: this.enumTranslator.getLabeledEnumValueArray(CalculationMethod).filter(le => ![CalculationMethod.Manual, CalculationMethod.Margin].includes(le.value as CalculationMethod)),
            calculationMethodsConverter: new LabeledEnumValueConverter(this.enumTranslator, CalculationMethod),
            documentName: '',
            documentKindValueConverter: new LabeledEnumValueConverter(this.enumTranslator, PurchaseDocumentKind),
            paymentMethods: [],
            paymentMethodsConverter: null,
            numberings: [],
            numberingsConverter: null,
        };
    }

    protected override async preloadDataAsync(): Promise<void> {
        await Promise.all([
            super.preloadDataAsync(),
            this.localContext.preload(PaymentMethodQuery),
            this.localContext.preload(NumberingQuery),
            this.localContext.preload(CompanyParametersQuery)
        ]);
    }

    protected override async prepareViewModelAsync(): Promise<void> {
        await super.prepareViewModelAsync();

        this.viewModel.documentName = this.enumTranslator.getLabeledEnumValue(PurchaseDocumentKind, this.viewModel.bo.model.name).label;

        const paymentMethods = await this.localContext.getPreloaded(PaymentMethodQuery) || [];
        this.viewModel.paymentMethods = paymentMethods;
        this.viewModel.paymentMethodsConverter = new IdentifiableValueConverter(paymentMethods);

        const numberings = await this.localContext.getPreloaded(NumberingQuery) || [];
        this.viewModel.numberings = numberings;
        this.viewModel.numberingsConverter = new IdentifiableValueConverter(numberings);

        const { vatPayer } = this.localContext.getPreloaded(CompanyParametersQuery)!;
        this.viewModel.isDefaultCalculationMethodVisible = (this.viewModel.bo.model.name !== PurchaseDocumentKind.EPurchaseInvoice)
            && ((this.viewModel.bo.model.name === PurchaseDocumentKind.PurchaseInvoice) || vatPayer);

        this.viewModel.isDefaultDueDateVisible = this.viewModel.bo.model.name === PurchaseDocumentKind.EPurchaseInvoice;
    }

    protected override get dictionaryToLoadProperties(): { id: number | null, [key: string]: string | number | null } | null {
        return this.data?.id ? { id: null, name: this.data.id } : null;
    }
}
